import { Shortcuts } from '@backstage-community/plugin-shortcuts';
import { FeatureFlagged } from '@backstage/core-app-api';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import { useApi, iconsApiRef } from '@backstage/frontend-plugin-api';
import { IconComponent } from '@backstage/core-plugin-api';
import { type NavContentComponentProps } from '@backstage/plugin-app-react';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { githubViewPermission } from '@internal/backstage-plugin-github-common';
import { SidebarLogo } from './SidebarLogo';
import { SidebarGroupLabel } from './SidebarGroupLabel';
import { TeamsSidebarItem } from './TeamsSidebar/TeamsSidebarItem';

export const SidebarContent = ({ navItems }: NavContentComponentProps) => {
  const iconsApi = useApi(iconsApiRef);
  const icon = (key: string): IconComponent => iconsApi.getIcon(key) as IconComponent;

  const nav = navItems.withComponent(item => (
    <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
  ));

  // Taken from the same `navItems` collection as `nav`, so taken state stays
  // unified (and `nav.rest()` keeps working). Rendered as a submenu item since
  // it lives inside the "Tools & Links" submenu, while reusing the plugin icon.
  const entityValidatorNavItem = navItems.take('page:varia-entity-validator');

  const docsSubmenu = (
    <SidebarSubmenu>
      <SidebarGroupLabel>Developer info</SidebarGroupLabel>
      <SidebarSubmenuItem
        title="Equinor Developer"
        to="docs/default/component/equinor-developer"
        icon={icon('libraryBooks')}
      />
      <SidebarSubmenuItem
        title="AppSec"
        to="docs/default/Component/appsec"
        icon={icon('verifiedUser')}
      />
      <SidebarGroupLabel>Platforms</SidebarGroupLabel>
      <SidebarSubmenuItem title="Varia" to="docs/default/system/varia/" icon={icon('varia')} />
      <SidebarSubmenuItem title="Omnia" to="docs/default/system/omnia" icon={icon('cloud')} />
      <SidebarSubmenuItem title="Radix" to="docs/default/system/radix" icon={icon('eco')} />
      <SidebarSubmenuItem
        title="Power Platform"
        to="docs/default/component/power-platform-equinor"
        icon={icon('batteryChargingFull')}
      />
      <SidebarSubmenuItem
        title="Design System"
        to="https://eds.equinor.com"
        icon={icon('palette')}
        dropdownItems={[
          {
            title: 'EDS Documentation',
            to: 'https://eds.equinor.com',
          },
          {
            title: 'EDS Figma',
            to: 'https://www.figma.com/files/682286909510540417/team/590517879490131675/Team?fuid=1492114553874128416',
          },
          {
            title: 'EDS Storybook',
            to: 'https://storybook.eds.equinor.com/?path=/docs/introduction--docs',
          },
          {
            title: 'EDS on Slack',
            to: 'https://equinor.slack.com/archives/CJT20H1B9',
          },
        ]}
      />
    </SidebarSubmenu>
  );

  const toolsSubmenu = (
    <SidebarSubmenu title="Tools & Links">
      <RequirePermission permission={githubViewPermission} errorPage={<></>}>
        {entityValidatorNavItem && (
          <SidebarSubmenuItem
            to={entityValidatorNavItem.href}
            icon={() => entityValidatorNavItem.icon}
            title={entityValidatorNavItem.title}
          />
        )}
      </RequirePermission>
      <SidebarSubmenuItem
        to="settings/feature-flags"
        icon={icon('flagOutlined')}
        title="Feature Flags"
      />
      <SidebarGroupLabel>External links</SidebarGroupLabel>
      <SidebarSubmenuItem
        to="https://app.powerbi.com/Redirect?action=OpenReport&appId=c509ae81-2bb9-4a54-a4b3-0a8cb4b7ee50&reportObjectId=9fdc4e0a-8912-482f-bf15-d9839d1e7525&ctid=3aa4a235-b6e2-48d5-9195-7fcf05b459b0&reportPage=ReportSection&pbi_source=appShareLink&portalSessionId=5216d674-e177-40d8-bd46-41c0ff9c5cf2"
        icon={icon('menuBook')}
        title="Abbreviations list"
      />
      <SidebarSubmenuItem
        to="https://chat.equinor.com"
        icon={icon('chat')}
        title="Equinor AI Chat"
      />
      <SidebarSubmenuItem
        to="https://apps.equinor.com/short"
        icon={icon('link')}
        title="Equinor Short URL"
      />
      <SidebarSubmenuItem
        to="https://secrets.equinor.com"
        icon={icon('verifiedUser')}
        title="Share Secrets"
      />
      <SidebarSubmenuItem
        to="https://techradar.equinor.com"
        icon={icon('trackChanges')}
        title="Techradar"
      />
    </SidebarSubmenu>
  );

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={iconsApi.icon('search')} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup>
        <SidebarItem icon={icon('varia')} to="docs/default/system/varia/" text="User Guide" />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={iconsApi.icon('menu')}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={icon('home')} to="home" text="Home" />
        <SidebarItem icon={icon('libraryBooks')} to="docs" text="Docs">
          {docsSubmenu}
        </SidebarItem>
        <SidebarItem icon={icon('book')} to="catalog" text="Catalog" />
        <RequirePermission permission={githubViewPermission} errorPage={<></>}>
          {nav.take('page:github-repositories')}
          {nav.take('page:github-codespaces')}
        </RequirePermission>
        {nav.take('page:radix')}
        <TeamsSidebarItem icon={icon('group')} />
        <FeatureFlagged with="notifications">
          <NotificationsSidebarItem />
        </FeatureFlagged>
        <SidebarItem icon={icon('kind:api')} to="api-docs" text="APIs" />
        <SidebarItem icon={icon('addCircleOutline')} to="create" text="Create..." />

        <SidebarItem icon={icon('build')} text="Tools & Links">
          {toolsSubmenu}
        </SidebarItem>
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <Shortcuts />
      <SidebarGroup label="Settings" icon={<UserSettingsSignInAvatar />} to="/settings">
        <RequirePermission permission={devToolsAdministerPermission} errorPage={<></>}>
          <SidebarItem icon={icon('build')} to="devtools" text="DevTools" />
        </RequirePermission>
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
  );
};
